import { gql } from '@apollo/client';
import {
  ATLAS_MAX_ADDRESSES_PER_RUN,
  AGENT_RED_MINUTES,
  AGENT_YELLOW_MINUTES,
  agentStaleLevel,
  type AgentRemovalRefusal,
  type AgentStaleLevel,
  type AtlasEntryState,
} from '@infras/shared-types';

/**
 * The agent lifecycle surfaces — the fleet's warning colours, the Overview
 * checklist's two new queries, and the root-only cascade removal.
 *
 * THE THRESHOLDS ARE NOT DEFINED HERE. `agentStaleLevel`, AGENT_YELLOW_MINUTES and
 * AGENT_RED_MINUTES come from `@infras/shared-types` — the same module the resolver
 * imports — for the reason `atlas.ts` states about the capacity numbers: a fleet list
 * that colours a row red at a different minute than the checklist counts it is not a
 * styling bug, it is two answers to "which agents have stopped reporting".
 *
 * They are ABSOLUTE MINUTES, never a multiple of the heartbeat interval. The server
 * hands the agent its next poll interval (`nextPollSeconds`, 60 s idle / 5 s busy),
 * so anything expressed in missed beats goes silently wrong the day that dial moves.
 *
 * ONE CLOCK, TWO STATES (owner decision, Binh, 2026-08-25). Warning past
 * AGENT_YELLOW_MINUTES, red past AGENT_RED_MINUTES, and DevOps acts on red from the
 * Overview checklist. The 4-hour "gone" clock that used to decide, separately, when
 * an address became reclaimable is DELETED from this app: two clocks meant every
 * screen had to explain which one it was speaking on, and the reclaim clock was the
 * one nobody was willing to wait out. Red is what colours a row, what carries a bulk
 * checkbox, what the checklist counts and what the remove preview pre-ticks.
 *
 * RED IS STILL NOT A DELETION. Colouring a row changes nothing on Atlas; the preview
 * judges every address on the server's `removable`, and the confirm re-judges it.
 */

export { ATLAS_MAX_ADDRESSES_PER_RUN, AGENT_RED_MINUTES, AGENT_YELLOW_MINUTES, agentStaleLevel };
export type { AgentRemovalRefusal, AgentStaleLevel };

/**
 * The refusal codes, in words.
 *
 * The wire carries the code and the code is what the audit trail records, so it stays
 * the identity — but a dialog that prints `shared_with_live_agent` at an operator mid
 * production delete is making them translate at the worst possible moment. Keyed on
 * the exported union, so a new refusal on the server is a TYPE error here rather than
 * a row that silently reads as its own enum value.
 */
export const REFUSAL_LABEL: Record<Exclude<AgentRemovalRefusal, LegacyRefusal>, string> = {
  open_to_all: 'this entry admits the whole internet — a security finding, not waste',
  // TWO GUARDS, AND THEY ARE THE ONLY TWO A USER SEES (owner decision, 2026-08-25).
  // "We could not read the list" and "no run touches more than the cap" are the whole
  // explanation this dialog owes anyone. The shared-address and cluster-quorum refusals
  // went with the shared-address model on the server side.
  atlas_state_unknown: "this project's access list could not be read",
  deferred_cap: `past the ${ATLAS_MAX_ADDRESSES_PER_RUN}-per-project cap for one run — re-offered next run`,
  not_linked: 'no link row says this agent ever put that address there',
};

/**
 * LEGACY refusal codes — no longer produced, still translated.
 *
 * `kind_unsupported` was 052's v1 answer: a cascade over a DigitalOcean firewall target
 * refused outright. e-32 replaced it with Atlas parity (the cascade removes the address
 * from the DO firewall like any other), so the server stops emitting the code — but the
 * audit trail keeps every code it ever wrote, and a removal run recorded before that
 * change still renders its outcomes here. Kept OUTSIDE the typed record on purpose:
 * that record is keyed on `AgentRemovalRefusal` so a NEW server refusal is a type error,
 * and a retired one must not hold that union open to keep this file compiling.
 */
type LegacyRefusal = 'kind_unsupported';

const LEGACY_REFUSAL_LABEL: Record<LegacyRefusal, string> = {
  kind_unsupported: 'this target is a DigitalOcean firewall — deletes are refused for do_db',
};

/** Words for a refusal, falling back to the raw code — an unknown code from a newer
 *  API must still be shown, never swallowed. */
export function refusalLabel(code?: string | null): string {
  if (!code) return '';
  return (
    REFUSAL_LABEL[code as Exclude<AgentRemovalRefusal, LegacyRefusal>] ??
    LEGACY_REFUSAL_LABEL[code as LegacyRefusal] ??
    code
  );
}

/** The one place each colour's rule is worded, so no screen can say it twice. */
export const YELLOW_RULE = `silent past ${AGENT_YELLOW_MINUTES} min`;
export const RED_RULE = `silent past ${AGENT_RED_MINUTES} min`;

/**
 * Sentinel for the fleet list's status dropdown, and the deep-link target the
 * Overview checklist's red-agents line points at. Not a value of `agents.status`: the
 * column reads `active` for an agent that has been silent for a month, so silence has
 * to be matched separately or picking it would filter everything away.
 */
export const RED_FILTER = '__red__';

/** The retired `__gone__` sentinel, kept ONLY as an alias so bookmarks and older
 *  links land on the red list instead of on an empty table. Nothing renders it. */
export const LEGACY_GONE_FILTER = '__gone__';

/** Same idea for `agent_projects.is_whitelisted = false` — a registration that
 *  failed leaves the agent `active` too; the failure is on the link row. */
export const REG_FAILED_FILTER = '__regfailed__';

/** And for Atlas drift: the condition lives on an Atlas row, not on `agents.status` — the
 *  agent is heartbeating and reads `active` (or, worse, `removed`) while its address is
 *  missing from the access list. */
export const DRIFT_FILTER = '__drift__';

/* The row tint and the red predicate live in `agentFleet.ts`, not here: both have to
 * know the `status` column as well as the clock (a removed agent is silent forever and
 * must stop counting as attention), and a silence-only version of either is exactly the
 * bug the first production bulk removal hit. */

// ── the wire ─────────────────────────────────────────────────────────────────
//
// Written against backend-agent's contract (`agent-lifecycle.types.ts` +
// `agents.resolver.ts` on this branch). Three rules from that file's header are
// honoured HERE rather than in the components:
//   1. `thresholdMinutes` ships with the red count — the checklist prints the server's
//      number instead of re-deriving one.
//   2. `inLiveList` is THREE-VALUED: null means the list could not be read, and must
//      render as unknown, never as "not listed".
//   3. `removable` is the field the selection keys on — never `state` alone. The link's
//      own `addressLive` was REMOVED server-side with the shared-address model; the
//      identically-named field on `atlasEgressIps` is a DIFFERENT one and still exists.
//   4. `ownAgentLive` is the "this node is still reporting" signal — NOT RED YET, on
//      the one clock this app has. Never re-derive it from `lastSeenAt`.

export interface AgentChecklistRow {
  agentId: string;
  clusterName: string;
  nodeName?: string | null;
  lastSeenAt: string;
  silentMinutes: number;
}

export interface FailedRegistration {
  agentId: string;
  clusterName: string;
  nodeName?: string | null;
  atlasProjectKey: string;
  ipCidr: string;
  lastError: string;
  lastAttemptAt?: string | null;
}

/**
 * One address a HEARTBEATING agent owns that Atlas does NOT have — a live node that cannot
 * reach the database right now.
 *
 * IT IS AN ALARM, AND NOTHING IS FIXING IT. This replaced `selfHeal`, which reported repairs
 * a job had already made. The owner ruled that out (Binh, 2026-08-25: "I want to keep
 * heartbeat simple, just tell us if the agent is alive or not. Recover or Prune or Clean up
 * will be DevOps job"), so the job DETECTS and a human REPAIRS. There is no armed/un-armed
 * split any more because there is no armed state: every row is open work, and the action is
 * the root-gated restore.
 *
 * `prunedBy` / `prunedAt` / `prunedReason` are the operator's most important context and the
 * reason they are on the wire: "someone deleted this deliberately 40 minutes ago" and "Atlas
 * lost it, nobody knows how" are the same symptom and different decisions. Null when no
 * suppression record explains the gap.
 *
 * `agentStatus === 'removed'` is the loudest case: a cascade settled the agent while its node
 * was — and still is — beating, so the fleet page is lying about that node and a restore also
 * puts the agent back to `active`.
 */
export interface AtlasDriftRow {
  agentId: string;
  clusterName: string;
  atlasProjectKey: string;
  ipCidr: string;
  agentStatus: string;
  agentLastSeenAt: string;
  detectedAt: string;
  prunedAt?: string | null;
  prunedBy?: string | null;
  prunedReason?: string | null;
}

export interface AtlasDriftSummary {
  /** DISTINCT ADDRESSES currently drifted, counted in SQL. */
  count: number;
  /** How far back the summary looks. From the server, so nothing here hard-codes it. */
  windowHours: number;
  rows: AtlasDriftRow[];
}

/**
 * One human removal a live agent's own report has OVERRIDDEN (migration 051).
 *
 * IT IS A NOTICE, NOT AN ALARM, AND NOT OPEN WORK — the exact opposite of
 * `AtlasDriftRow` above, which is why the two must never share a renderer or a
 * severity. Nothing failed here. A person took an address off the access list; a node
 * that is still running reported it again, and the address is effective again, which
 * is CORRECT — a live node must not stay locked out of the database. There is no
 * resolve action and there is nothing to fix.
 *
 * What the row records is that a person's decision was overridden by a machine, and
 * WHO that person was. Until 051 that transition left no trace at all: the clearing
 * `ON CONFLICT` nulled `pruned_at`/`pruned_by`/`pruned_reason`, so afterwards the
 * database could only answer that no removal had ever happened — which is what it
 * answered, repeatedly, on prod on 2026-08-26 after the owner removed socket-io-v1's
 * addresses and the agent re-registered minutes later.
 *
 * `prunedAt` / `prunedBy` / `prunedReason` are the OVERRIDDEN prune's facts, deliberately
 * named without the `superseded` prefix on the wire so the same three fields render with
 * the same markup as a drift row's provenance. `supersededAt` is when the agent's report
 * overrode it; the interval between the two is the operator's whole story — minutes means
 * a removal was undone before anyone could act on it, days means a node came back.
 */
export interface SupersededSuppressionRow {
  ipCidr: string;
  atlasProjectKey: string;
  clusterName?: string | null;
  nodeName?: string | null;
  /** When the agent's report overrode the removal. */
  supersededAt: string;
  /** The overridden removal's own three fields. */
  prunedAt?: string | null;
  prunedBy?: string | null;
  prunedReason?: string | null;
}

export interface SupersededSuppressionSummary {
  /** DISTINCT ADDRESSES, counted in SQL. */
  count: number;
  /** How far back the notice looks — 7 days, longer than drift's 6 hours because nothing
   *  ages a supersede out and there is no action to take. From the server, so nothing
   *  here hard-codes it. */
  windowHours: number;
  rows: SupersededSuppressionRow[];
}

/**
 * One access-list entry that admits the whole internet — a `/0`.
 *
 * IT IS AN ALARM AND IT IS THE TOP LINE. Not because it is urgent in the "page someone"
 * sense, but because everything else this feature measures is decoration while it is
 * true: a project at 196 of 200 careful per-node entries, with a `/0` alongside them, is
 * not nearly-full, it is open. So it sorts above every other item regardless of count —
 * the checklist's severity-then-count rule would otherwise put a 196-entry capacity
 * warning above a single `/0`.
 *
 * IT IS ALSO WHY THE EGRESS TABLE COULD GO. That table was the only place a `/0` appeared,
 * as one row in 458 behind an entry-state filter nobody set — i.e. it was discoverable in
 * principle and invisible in practice. This line is the condition of the removal.
 *
 * WHAT THIS APP WILL NOT DO IS REMOVE IT. `open_to_all` is not in
 * `ATLAS_RECLAIMABLE_STATES`, `prune.ts` refuses it by name ("a security finding for a
 * human, not prune fodder") and the project drawer therefore renders no checkbox on that
 * row. So the line links to the entry IN CONTEXT — the project's access list, filtered to
 * the address, where its Atlas comment usually says who opened it and why — and says
 * plainly that the removal itself is a decision taken in Atlas. Wording it as "remove it
 * here" would send an operator to hunt for a control that does not exist.
 */
export interface OpenToAllRow {
  atlasProjectKey: string;
  /** Always a `/0`. */
  ipCidr: string;
  /** Usually the only record of who opened the list and what for. */
  comment?: string | null;
  /** When the STORED list this came from was last read from Atlas — provenance, not now. */
  listReadAt: string;
}

export interface OpenToAllSummary {
  /** Entries found across every project whose list HAS been read. */
  count: number;
  rows: OpenToAllRow[];
  /**
   * Projects whose access list has never been read. ZERO HAS TWO MEANINGS and this is
   * what separates them: `count 0` with this list non-empty is "we could not look", not
   * "nothing found". Rendering the first as the second turns the detector into a false
   * assurance, so the line stays on screen — amber rather than red — until someone can
   * see into those projects. Both empty is the only healthy state, and renders nothing
   * (AC-16).
   */
  unreadableProjects: string[];
}

/**
 * Whether the `/0` line renders at all, and how loudly — the whole rule in one place.
 *
 * A function rather than a condition inlined in the JSX because all three answers are
 * load-bearing and only one of them is obvious:
 *   • entries found            → `open-red`,   an alarm;
 *   • none found, but a list we could not read → `open-amber`, because "we did not look"
 *     must not render as "clean";
 *   • nothing found and nothing unread        → `null`, THE ZERO CASE (AC-16): the
 *     checklist has no placeholder row, no green tick and no heading of its own.
 *
 * Returning the line kind rather than a boolean keeps the severity and the visibility
 * decision in one expression, so a future edit cannot make the line appear without
 * deciding what colour it is. `check-cluster-access.mjs` pins all three.
 */
export function openToAllLineKind(summary?: OpenToAllSummary): OverviewLineKind | null {
  if (!summary) return null;
  if (summary.count > 0) return 'open-red';
  if (summary.unreadableProjects.length > 0) return 'open-amber';
  return null;
}

/**
 * How an Overview line is rendered, and the ONLY place the three are told apart.
 *
 * `open-red` and `open-amber` are the checklist's two severities: someone must act, and
 * the line disappears when they have. `notice` is neither — it is history, it has no
 * resolve action, and it is not on the checklist at all.
 *
 * A function rather than three literals sprinkled through the JSX because the one way
 * this feature can still go wrong is somebody later "tidying" the notice into the
 * checklist's classes. `check-agent-status.mjs` pins all three as pairwise distinct and
 * pins that the notice carries neither open-item class.
 */
export type OverviewLineKind = 'open-red' | 'open-amber' | 'notice';

/**
 * A server-sent window, in words. Whole days read as days, anything else as hours.
 *
 * It exists so the notice can say "7 days" without any screen writing a 7, a 168 or a
 * 24-hour conversion of its own: the number is still entirely the server's, and the day
 * the window moves to 12 h the sentence follows it. The drift line prints hours directly
 * because 6 h is already the natural unit for it.
 */
export function formatWindow(hours: number): string {
  if (hours >= 24 && hours % 24 === 0) {
    const d = hours / 24;
    return `${d} day${d === 1 ? '' : 's'}`;
  }
  return `${hours} h`;
}

export function overviewLineClass(kind: OverviewLineKind): string {
  if (kind === 'open-red') return 'unowned-note unowned-note--red';
  if (kind === 'open-amber') return 'unowned-note';
  return 'notice-note';
}

export interface AgentChecklist {
  redAgents: {
    count: number;
    clusterCount: number;
    /** Absolute minutes, from the server. Printed, never recomputed. */
    thresholdMinutes: number;
    agents: AgentChecklistRow[];
  };
  failedRegistrations: { count: number; rows: FailedRegistration[] };
  atlasDrift: AtlasDriftSummary;
  /** Human removals a live agent has overridden. Not open work — see the row type. */
  suppressionSuperseded: SupersededSuppressionSummary;
  /** Access lists open to the internet. The checklist's top line — see the row type. */
  openToAll: OpenToAllSummary;
}

/**
 * The Overview checklist's two NEW lines. Everything else on that checklist already
 * had a query — `navCounts`, `atlasProjectsOverview`, `atlasUnmappedClusters` — and
 * is read from those rather than duplicated into this one.
 *
 * The fleet page reads it too, for the `__regfailed__` filter: the failure lives on
 * the link row, not on the agent, so the agent list alone cannot answer it.
 */
export const AGENT_CHECKLIST = gql`
  query AgentChecklist {
    agentChecklist {
      redAgents {
        count
        clusterCount
        thresholdMinutes
        agents {
          agentId
          clusterName
          nodeName
          lastSeenAt
          silentMinutes
        }
      }
      failedRegistrations {
        count
        rows {
          agentId
          clusterName
          nodeName
          atlasProjectKey
          ipCidr
          lastError
          lastAttemptAt
        }
      }
      atlasDrift {
        count
        windowHours
        rows {
          agentId
          clusterName
          atlasProjectKey
          ipCidr
          agentStatus
          agentLastSeenAt
          detectedAt
          prunedAt
          prunedBy
          prunedReason
        }
      }
      suppressionSuperseded {
        count
        windowHours
        rows {
          ipCidr
          atlasProjectKey
          clusterName
          nodeName
          supersededAt
          prunedAt
          prunedBy
          prunedReason
        }
      }
      openToAll {
        count
        unreadableProjects
        rows {
          atlasProjectKey
          ipCidr
          comment
          listReadAt
        }
      }
    }
  }
`;

/** One un-released link row: this agent put this address on this project's list. */
export interface AgentRemovalLink {
  agentId: string;
  atlasProjectKey: string;
  projectDisplayName?: string | null;
  ipCidr: string;
  isWhitelisted: boolean;
  state?: AtlasEntryState | null;
  /** null = the project's list could not be read. Unknown is not "absent". */
  inLiveList?: boolean | null;
  /**
   * Has THIS row's own node not gone silent yet? NOT RED (silent less than
   * AGENT_RED_MINUTES), computed server-side — never re-derived here, which is the bug
   * the field exists to fix: on the old 4 h window a node dead for 20 minutes was
   * called "still reporting", and every one of the eight heavy-job dialogs on
   * 2026-08-24 carried a warning that was factually wrong.
   *
   * Advice, not a gate. `removable` still decides what the server would act on.
   */
  ownAgentLive: boolean;
  /** THE selection predicate — server-computed, never re-derived here. */
  removable: boolean;
  /** Why not, when `removable` is false. */
  refusal?: string | null;
}

export interface AgentRemovalAgent {
  agentId: string;
  clusterName: string;
  nodeName?: string | null;
  status: string;
  lastSeenAt: string;
  links: AgentRemovalLink[];
}

/** One (agent, project, address) named back to confirm. All three are required: in a
 *  bulk run the same address can appear under more than one agent, and the audit trail
 *  records which agent's leg was released. */
export interface AgentRemovalTarget {
  agentId: string;
  atlasProjectKey: string;
  ipCidr: string;
}

export interface AgentRemovalOutcome {
  agentId: string;
  atlasProjectKey: string;
  ipCidr: string;
  /** `removed` · `already_absent` · `refused` · `failed` · `not_attempted` · `not_confirmed` */
  outcome: string;
  reason?: string | null;
}

export interface AgentSettleOutcome {
  agentId: string;
  status: string;
  reason?: string | null;
}

export interface AgentRemovalResult {
  dryRun: boolean;
  agents: AgentRemovalAgent[];
  outcomes: AgentRemovalOutcome[];
  settled: AgentSettleOutcome[];
  unknownAgentIds: string[];
}

const REMOVAL_FIELDS = `
  dryRun
  unknownAgentIds
  agents {
    agentId
    clusterName
    nodeName
    status
    lastSeenAt
    links {
      agentId
      atlasProjectKey
      projectDisplayName
      ipCidr
      isWhitelisted
      state
      inLiveList
      ownAgentLive
      removable
      refusal
    }
  }
  outcomes {
    agentId
    atlasProjectKey
    ipCidr
    outcome
    reason
  }
  settled {
    agentId
    status
    reason
  }
`;

/**
 * Which preview rows arrive TICKED.
 *
 * `removable` is the authority and it is the server's — whatever the server refuses is
 * never pre-ticked. On top of that, a row whose OWN node has not gone silent yet has
 * to be opted into: the server permits it (an agent's own liveness is deliberately not
 * a veto) but nothing re-adds the address afterwards.
 *
 * "Still reporting" is `ownAgentLive` — NOT RED YET, the same clock `isBulkSelectable`
 * keys on, so the pre-ticked set and the bulk-selectable set are the same set by
 * construction rather than by coincidence. Deriving it from the old 4 h window is what
 * made the 2026-08-24 heavy-job reclaim unusable: eight deleted droplets, all silent
 * well under 4 h, arrived unticked behind a warning that was factually wrong.
 *
 * Exported as a pure function so `scripts/check-agent-status.mjs` can pin it without a
 * browser — the default tick of a production delete dialog is worth a test.
 */
export function isPreTicked(l: Pick<AgentRemovalLink, 'removable' | 'ownAgentLive'>): boolean {
  return l.removable && !l.ownAgentLive;
}

/**
 * Preview. Reads Atlas, writes nothing — and it IS the "Atlas footprint" panel: the
 * preview is the only surface that shows what an agent owns, so nobody can reach the
 * removal without having seen it. Root only, like the mutation it is part of.
 */
export const REMOVE_AGENTS_PREVIEW = gql`
  mutation RemoveAgentsPreview($agentIds: [String!]!) {
    removeAgents(agentIds: $agentIds, dryRun: true) {
      ${REMOVAL_FIELDS}
    }
  }
`;

/**
 * Confirm. `confirm` is an ALLOW-LIST, not a filter: every triple is re-classified
 * against a fresh read of Atlas inside the same call, so a row that stopped being
 * removable since the preview is refused rather than deleted. That is why the dialog
 * does not re-preview before confirming — and why `refused` and `failed` must stay two
 * different outcomes on the way back.
 */
export const REMOVE_AGENTS_CONFIRM = gql`
  mutation RemoveAgentsConfirm($agentIds: [String!]!, $confirm: [AgentRemovalTargetInput!]!) {
    removeAgents(agentIds: $agentIds, dryRun: false, confirm: $confirm) {
      ${REMOVAL_FIELDS}
    }
  }
`;

// ── Restore — the human counterpart of Remove ────────────────────────────────
//
// Same shape as the removal wire on purpose: preview (dryRun) → confirm, both through the
// same mutation, so an operator learns ONE pattern. The differences are the ones that
// matter: a restore names (project, address) with no agent — the address is the subject and
// the agents behind it are evidence — and its preview outcome is `would_restore`, an
// answer, not an empty list waiting for a confirm.
//
// It exists because NOTHING ELSE brings an address back: `atlasHealthy` is sticky-true so a
// healthy agent never re-registers, reconcile is add-only and off in production, and the
// owner ruled out automatic repair. Root only, the same gate as Remove — the same person who
// may delete from a production access list is the one who may put something back on it.

/** One (project, address) to put back. No agent: the address is the subject. */
export interface AtlasRestoreTarget {
  atlasProjectKey: string;
  ipCidr: string;
}

export interface AtlasRestoreRow {
  atlasProjectKey: string;
  ipCidr: string;
  /** Agents on record as having put this address there — released link rows included,
   *  because undoing a cascade is the point. */
  agentIds: string[];
  /** THE evidence the operator decides on. Null when no agent row survives. */
  agentLastSeenAt?: string | null;
  agentStatus?: string | null;
  /** THREE values: null = the list could not be read (and the row is refused),
   *  false = Atlas does not have it, true = it is already there. */
  inLiveList?: boolean | null;
  /** Who took it off, when, and why. The whole reason to restore rather than shrug. */
  prunedAt?: string | null;
  prunedBy?: string | null;
  prunedReason?: string | null;
  /** `restored` · `would_restore` · `failed` · `refused` · `deferred_cap` */
  outcome: string;
  reason?: string | null;
}

export interface AtlasRestoreResult {
  dryRun: boolean;
  rows: AtlasRestoreRow[];
  /** Agents taken out of `removed` by this run — the cascade's settle, undone. The fleet
   *  view changes underneath the operator, so the result must say so. */
  reactivatedAgentIds: string[];
}

const RESTORE_FIELDS = `
  dryRun
  reactivatedAgentIds
  rows {
    atlasProjectKey
    ipCidr
    agentIds
    agentLastSeenAt
    agentStatus
    inLiveList
    prunedAt
    prunedBy
    prunedReason
    outcome
    reason
  }
`;

/** Preview. Reads Atlas, writes nothing — and it is the only surface that shows the
 *  provenance of a gap, so nobody reaches the restore without having seen who removed it. */
export const RESTORE_PREVIEW = gql`
  mutation RestoreAddressesPreview($targets: [AtlasRestoreTargetInput!]!) {
    restoreAgentAddresses(targets: $targets, dryRun: true) {
      ${RESTORE_FIELDS}
    }
  }
`;

/** Confirm. Every target is re-classified against a fresh read of Atlas inside the same
 *  call, so a row that stopped being restorable since the preview is refused, not written. */
export const RESTORE_CONFIRM = gql`
  mutation RestoreAddressesConfirm($targets: [AtlasRestoreTargetInput!]!) {
    restoreAgentAddresses(targets: $targets, dryRun: false) {
      ${RESTORE_FIELDS}
    }
  }
`;

/**
 * Which preview rows arrive TICKED.
 *
 * `would_restore` is the server's own "I would do this", so it is the only affirmative
 * answer and the only default tick. Everything else — `refused` (our verdict, including the
 * unreadable-list case and an address no agent still reports), `deferred_cap` (the run was
 * bounded) — arrives unticked with its reason beside it.
 *
 * A row whose address Atlas ALREADY has is still `would_restore`, and that is deliberate:
 * the Atlas half is a no-op there, but the database halves are not — that row is how a
 * cascade's `removed` agent gets its `active` back. So `inLiveList === true` is rendered as
 * context, never as a refusal.
 *
 * Pure, so `check-agent-status.mjs` can pin the default tick of a production write without
 * a browser — the same reason `isPreTicked` is pure for Remove.
 */
export function isRestorePreTicked(r: Pick<AtlasRestoreRow, 'outcome'>): boolean {
  return r.outcome === 'would_restore';
}

/** The restore outcomes, in words. Distinct from the removal labels: `refused` on a restore
 *  is not the same event as `refused` on a delete, and one map for both would make the two
 *  dialogs quietly say each other's sentences. */
export const RESTORE_OUTCOME_LABEL: Record<string, string> = {
  restored: 'put back on the access list',
  would_restore: 'would be put back',
  failed: 'Atlas rejected the add — still missing, retry',
  refused: 'kept off on purpose',
  deferred_cap: `past the ${ATLAS_MAX_ADDRESSES_PER_RUN}-address cap for one run — re-offered next run`,
};

export function restoreOutcomeLabel(outcome: string): string {
  return RESTORE_OUTCOME_LABEL[outcome] ?? outcome;
}
